import React from 'react';
import { Button, Flex, Text } from 'core/components';
import { InputGroup } from 'core/form';
import { observer } from 'mobx-react';
import type BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import { TextProps } from 'core/components/Text';

interface Props {
  panel: BoardPanelModel;
  title?: string;
  canEdit?: boolean;
  onSave?: (title: string) => void;
}

function PanelTitle({ panel, title = panel.title, canEdit: _canEdit, onSave }: Props) {
  const [editing, setEditing] = React.useState(false);
  const [editingTitle, setEditingTitle] = React.useState(title);
  const canEdit = _canEdit ?? panel.board.canEdit;

  const handleTitleClick = () => {
    setEditingTitle(title);
    setEditing(true);
  };

  const handleInputChange = (event: React.InputEvent) => {
    if (event.target instanceof HTMLInputElement) {
      setEditingTitle(event.target.value);
    }
  };

  const handleDoneEditing = () => {
    setEditing(false);
    const newTitle = editingTitle.trim();
    if (newTitle !== title) {
      if (onSave) {
        onSave(newTitle);
      } else {
        panel.save({ title: newTitle }, { draft: true, optimistic: true });
      }
    }
  };

  const handleCancelEditing = () => {
    setEditing(false);
    setEditingTitle(title);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleDoneEditing();
    } else if (event.key === 'Escape') {
      handleCancelEditing();
    }
  };

  const handleInputBlur = () => {
    handleDoneEditing();
  };

  const isSection = panel.get('type') === 'section';
  const textStyles: Partial<TextProps> = {
    fontWeight: 500,
    fontSize: isSection ? 18 : undefined
  };

  if (!canEdit) {
    return <Text {...textStyles}>{title}</Text>;
  }

  return (
    <Flex className="no-drag" alignItems="center" flex={editing ? 1 : undefined} minWidth={0}>
      {editing ? (
        <InputGroup
          autoFocus
          value={editingTitle}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          placeholder="Title..."
          fill
          style={{
            minWidth: `min(100%, ${Math.max(editingTitle.length, 12) + 4}ch)`
          }}
        />
      ) : (
        <Button minimal textAlign="left" onClick={handleTitleClick} mx="-4px" style={{ cursor: 'text' }}>
          <Text {...textStyles}>{title}</Text>
        </Button>
      )}
    </Flex>
  );
}

export default observer(PanelTitle);
